import {
  ANONYMITY_COPY,
  HELD_GIFT_DEFAULT_HOLD_DAYS,
  heldGiftTermsSentence,
} from "@givehere/contracts";
import { LegalLink, LegalSection, LegalShell, SupportContact } from "./LegalShell.js";
import { PLATFORM_MAILING_ADDRESS, useSite } from "./site.js";

/**
 * Terms of Use (GH-812). A draft for counsel, written from what the product does today.
 *
 * Three rules the wording follows, each from a decision rather than from taste:
 *
 *  - **The gift is to the fund, in support of an organization** (DEC-002, DEC-028). Never "to" the
 *    organization and never a promise of delivery: the donee is the entity, which keeps discretion
 *    and control, and the organization has no entitlement until a grant is approved. No
 *    donor-advised-fund vocabulary ("advise", "variance power") — DEC-028 explains why that
 *    vocabulary is harmful on the direct-gift lane.
 *  - **Refunds correct mistakes; they do not return a completed gift** (DEC-006 as amended, DEC-021).
 *  - **A pledge is a promise, not received funds** (CLAUDE.md).
 *
 * The held-gift sentence is DEC-046 answer 2, quoted from the function the give screens use.
 */
export function TermsPage() {
  const { organizationLegalName: entity, productName: product } = useSite();
  return (
    <LegalShell title="Terms of Use">
      <LegalSection id="about" title="About these terms">
        <p>
          These terms govern your use of the {product} website and apps, which are operated by{" "}
          {entity} (&ldquo;we&rdquo;, &ldquo;us&rdquo;). By using {product} or giving through it,
          you agree to them. If you do not agree, please do not use {product}.
        </p>
      </LegalSection>

      <LegalSection id="platform" title="What the platform is">
        <p>
          {product} lets people give to nonprofit organizations — at their events, on their pages,
          and from a directory. Organizations are listed only when they appear in IRS records as
          tax-exempt. Some listings are drawn from public records and have not yet been claimed by
          the organization; those are marked as unclaimed.
        </p>
        <p>
          You do not need an account to give. An account is optional and keeps your receipts
          together.
        </p>
      </LegalSection>

      <LegalSection id="gifts" title="How a gift works">
        <p>
          When you give through {product}, your gift is a charitable contribution to {entity}, made
          in support of the organization you choose. {entity} receives the gift and is the merchant
          of record for the payment: the charge is made by {entity}, and your statement shows{" "}
          <strong>GIVEHERE.CO</strong>.
        </p>
        <p>
          {entity} then makes a grant to the organization you chose. {entity} keeps discretion and
          control over every gift, and your choice of organization — and of any fund or purpose
          within it — is honoured as a preference. The organization has no claim to a particular
          gift until {entity} approves the grant.
        </p>
        <p>
          If a grant cannot be made — for example, the organization loses its tax-exempt status,
          stops operating, or cannot be paid — {entity} will tell you and offer you another
          organization to receive it; if we cannot reach you, the gift is used for a similar
          charitable purpose.
        </p>
        <p>
          A completed gift is final. It is not a deposit and there is no balance you can withdraw.
          The only exceptions are the corrections described under{" "}
          <a href="#refunds">Refunds and disputes</a>.
        </p>
      </LegalSection>

      <LegalSection id="fee" title="The platform fee">
        <p>
          Every gift carries a platform fee that pays for running {product}, including payment
          processing. You may add the fee on top of your gift so that the whole gift supports the
          organization; if you do not, it is deducted from the grant. The fee is shown before you
          confirm. Current amounts are on the{" "}
          <LegalLink to="disclosures" hash="deducted">
            Disclosures
          </LegalLink>{" "}
          page.
        </p>
      </LegalSection>

      <LegalSection id="donor-obligations" title="Your responsibilities">
        <ul>
          <li>Use only payment methods you are authorized to use.</li>
          <li>Give accurate information, including any email address or name you provide.</li>
          <li>Be at least 18 years old.</li>
          <li>
            Do not use {product} for fraud, to test payment cards, to move money for anyone else, or
            for anything unlawful.
          </li>
          <li>
            Keep anything you publish — a display name, a dedication, a prompt answer, a gift note
            you ask to show on an event screen — free of abuse, impersonation, and other
            people&rsquo;s private information. We may remove it.
          </li>
          <li>
            Keep your sign-in methods secure; you are responsible for activity on your account.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="receipts" title="Receipts">
        <p>
          When a gift completes, you see a receipt on screen, and signed-in donors keep it in their
          account. Where you give an email address, a copy is sent to it. The receipt names {entity}{" "}
          as the recipient and states whether you received goods or services for the gift.
        </p>
        <p>
          A ticket is a purchase of admission, not a donation, and its receipt says so. A grant you
          recommend from a donor-advised fund is a gift from your fund&rsquo;s sponsor, which issues
          any acknowledgment. We do not give tax advice; ask your adviser how a gift affects your
          taxes.
        </p>
      </LegalSection>

      <LegalSection id="refunds" title="Refunds and disputes">
        <p>
          Because a completed gift is final, refunds are for correcting a transaction that should
          not have happened: a duplicate charge, a wrong amount, or a payment you did not authorize.
          A change of mind is not a reason for a refund.
        </p>
        <ul>
          <li>
            Ask within 30 days of the gift, by contacting <SupportContact /> or the organization.
            Refunds are issued by {product} staff or the organization&rsquo;s authorized staff, not
            through a self-service form.
          </li>
          <li>
            A refund returns your gift to the original payment method, minus the platform fee for
            that gift, which is kept: it pays the card processor, and the processor does not return
            its own fee when a payment is refunded.
          </li>
          <li>
            Ticket refunds are decided by the event&rsquo;s organizer under its own policy and are
            issued through {product}.
          </li>
          <li>
            If you dispute a charge with your card issuer or bank, we respond with the gift&rsquo;s
            record. Please contact us first — most problems are resolved faster that way.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="pledges" title="Pledges">
        <p>
          A pledge is a promise to give later. Making one charges nothing, and a pledge is not a
          gift: it is never counted as money received, and an organization&rsquo;s totals show
          pledges separately from gifts. You may pay a pledge later through {product}; until you do,
          nothing has been given. A pledge is a statement of intent, not a binding promise: you are
          not legally obligated to pay it.
        </p>
      </LegalSection>

      <LegalSection
        id="unclaimed"
        title="Gifts to organizations that have not claimed their listing"
      >
        <p>
          Where this option is offered, a gift to an unclaimed organization is made to {product} /{" "}
          {entity} and is targeted for that organization, and its receipt says so.
        </p>
        <blockquote className="legal__quote">
          {heldGiftTermsSentence(HELD_GIFT_DEFAULT_HOLD_DAYS)}
        </blockquote>
        <p>No platform fee is deducted from a gift sent by check.</p>
      </LegalSection>

      <LegalSection id="recognition" title="Public recognition">
        <p>
          Gifts are anonymous to the public unless you choose to be named. Choosing a name, a
          dedication, or an &ldquo;in honor of&rdquo; line is optional, and we may moderate what is
          shown publicly.
        </p>
        <p>
          A note you write with a gift is private to the organization unless you tick &ldquo;Show on
          the event screen&rdquo; for that gift. A ticked note is a request, not a guarantee: the
          organization decides whether to approve it, may decline it or take it down at any time,
          and nothing appears until it is approved. An approved note is shown beside your public
          name if you chose to be named, and with no name if you gave anonymously.
        </p>
        <p>
          Anonymity is anonymity to the public. The organization you give to sees your gift and who
          you are, so it can thank you. If you would rather no organization know who you are, turn
          on &ldquo;{ANONYMITY_COPY.organizationsLabel}&rdquo; in your profile; how it works is in
          the{" "}
          <LegalLink to="privacy" hash="anonymous">
            Privacy Policy
          </LegalLink>
          .
        </p>
      </LegalSection>

      <LegalSection id="third-parties" title="Payment and sign-in providers">
        <p>
          Payments are processed by Stripe (cards, Apple Pay, Google Pay) and, for bank payments,
          Plaid. Donor-advised fund grants are made through Chariot&rsquo;s DAFpay and governed by
          your fund sponsor&rsquo;s terms. You may sign in with Apple or Google. Each
          provider&rsquo;s own terms also apply to your use of it. How we handle your information is
          in the <LegalLink to="privacy">Privacy Policy</LegalLink>.
        </p>
      </LegalSection>

      <LegalSection id="service" title="Changes to the service">
        <p>
          We may change, suspend, or end any part of {product}, refuse or reverse a transaction we
          reasonably believe is fraudulent or unlawful, and suspend access that breaks these terms.
        </p>
      </LegalSection>

      <LegalSection id="liability" title="Disclaimers and limitation of liability">
        <p>
          {product} is provided &ldquo;as is&rdquo;. To the fullest extent the law allows, we make
          no warranties beyond those stated here, and we are not liable for indirect, incidental, or
          consequential losses. Our total liability for any claim is limited to the greater of $100
          or the platform fees you paid in the 12 months before the claim. Nothing in these terms
          limits a liability that cannot be limited by law.
        </p>
        <p>
          Information about organizations comes from public records and from the organizations
          themselves. We check it, but we do not guarantee it.
        </p>
      </LegalSection>

      <LegalSection id="law" title="Governing law">
        <p>
          These terms are governed by the laws of the State of Florida. Disputes are resolved in the
          state or federal courts located in Broward County, Florida.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="Changes to these terms">
        <p>
          We will post changes here and update the date above. A change applies to gifts made after
          it is posted.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="Contact">
        <p>
          {entity}, {PLATFORM_MAILING_ADDRESS}. Questions: <SupportContact />.
        </p>
      </LegalSection>
    </LegalShell>
  );
}
